import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  Clock,
  Plus,
  X,
  Check,
  AlertCircle,
  Sparkles,
  Image as ImageIcon,
  Send,
  Trash2,
  Edit3,
  RefreshCw,
  Twitter,
  Linkedin,
  Facebook,
  Instagram,
  Hash,
  Link2,
  Eye,
  ChevronDown,
  Loader2,
} from 'lucide-react';
import { useAuthStore, updateCredits } from '../store/auth';
import { useThemeStore } from '../store/theme';
import DateTimePicker from './DateTimePicker';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Helper to check if image URL is from OpenAI's DALL-E (expires after ~1 hour)
// These URLs contain blob.core.windows.net and have SAS token expiration
const isExpiredDalleUrl = (url: string): boolean => {
  if (!url) return false;
  // Check if it's an OpenAI DALL-E URL (Azure Blob Storage)
  if (url.includes('oaidalleapiprodscus.blob.core.windows.net')) {
    // Check if the URL has an expired SAS token
    const seMatch = url.match(/se=([^&]+)/);
    if (seMatch) {
      try {
        const expiryDate = new Date(decodeURIComponent(seMatch[1]));
        return expiryDate < new Date();
      } catch {
        // If we can't parse the date, assume it's expired (old URL format)
        return true;
      }
    }
    // No expiry found, assume it might be expired
    return true;
  }
  return false;
};

// Get a safe image URL - returns empty string for expired DALL-E URLs
const getSafeImageUrl = (url: string | null): string => {
  if (!url) return '';
  if (isExpiredDalleUrl(url)) return '';
  return url;
};

interface ScheduledPost {
  id: number;
  title: string | null;
  content: string;
  generated_content: string | null;
  image_url: string | null;
  generated_image_url: string | null;
  scheduled_at: string;
  timezone: string;
  status: 'draft' | 'scheduled' | 'posting' | 'posted' | 'failed' | 'cancelled';
  target_platforms: string[];
  target_integration_ids: string[] | null;
  platform_content: Record<string, string>;
  hashtags: string[];
  mentions: string[];
  link_url: string | null;
  ai_tone: string;
  ai_style: string;
  content_type: string;
  post_results: Record<string, { success: boolean; post_id?: string; error?: string }>;
  error_message: string | null;
  created_at: string;
}

interface SocialIntegration {
  id: number;
  provider: string;
  name: string;
  status: string;
  provider_username: string | null;
  platform_info: {
    name: string;
    icon: string;
    color: string;
    maxLength?: number;
  };
}

// API Response types
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface PostsResponse {
  posts: ScheduledPost[];
  total: number;
  page: number;
  limit: number;
}

interface PostStats {
  total: number;
  scheduled: number;
  posted: number;
  failed: number;
  drafts: number;
  upcoming_week: number;
}

interface GeneratedContent {
  main_content: string;
  short_version: string;
  professional_version: string;
  casual_version: string;
  hashtags: string[];
  hook: string;
  cta: string;
}

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const platformIcons: Record<string, React.ReactNode> = {
  twitter: <Twitter className="w-4 h-4" />,
  linkedin: <Linkedin className="w-4 h-4" />,
  facebook: <Facebook className="w-4 h-4" />,
  instagram: <Instagram className="w-4 h-4" />,
};

const platformColors: Record<string, string> = {
  twitter: '#1DA1F2',
  linkedin: '#0A66C2',
  facebook: '#1877F2',
  instagram: '#E4405F',
  tiktok: '#000000',
  pinterest: '#E60023',
};

export default function ScheduledPosts() {
  const { token } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  // State
  const [posts, setPosts] = useState<ScheduledPost[]>([]);
  const [stats, setStats] = useState<PostStats | null>(null);
  const [integrations, setIntegrations] = useState<SocialIntegration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'scheduled' | 'posted' | 'draft' | 'failed'>('all');

  // Create/Edit Modal
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState<ScheduledPost | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    content: '',
    generated_content: '',
    image_url: '',
    generated_image_url: '',
    scheduled_at: '',
    target_platforms: [] as string[],
    hashtags: [] as string[],
    link_url: '',
    ai_tone: 'engaging',
    ai_style: 'human',
  });
  const [hashtagInput, setHashtagInput] = useState('');

  // AI Generation
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateTopic, setGenerateTopic] = useState('');

  // Preview
  const [previewPlatform, setPreviewPlatform] = useState<string>('twitter');
  const [showPreview, setShowPreview] = useState(false);

  // Load data
  const loadPosts = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/scheduled-posts?status=${filter}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json() as ApiResponse<PostsResponse>;
      if (data.success && data.data) {
        setPosts(data.data.posts);
      }
    } catch (err) {
      console.error('Failed to load posts:', err);
    }
  }, [token, filter]);

  const loadStats = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/scheduled-posts/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json() as ApiResponse<PostStats>;
      if (data.success && data.data) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, [token]);

  const loadIntegrations = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/scheduled-posts/integrations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json() as ApiResponse<SocialIntegration[]>;
      if (data.success && data.data) {
        setIntegrations(data.data);
      }
    } catch (err) {
      console.error('Failed to load integrations:', err);
    }
  }, [token]);

  useEffect(() => {
    const loadAll = async () => {
      setIsLoading(true);
      await Promise.all([loadPosts(), loadStats(), loadIntegrations()]);
      setIsLoading(false);
    };
    loadAll();
  }, [loadPosts, loadStats, loadIntegrations]);

  // Generate content with AI
  const generatePost = async () => {
    if (!generateTopic || !token) return;
    setIsGenerating(true);
    try {
      const res = await fetch(`${API_URL}/scheduled-posts/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          topic: generateTopic,
          tone: form.ai_tone,
          style: form.ai_style,
          platforms: form.target_platforms,
          include_hashtags: true,
          include_emojis: true,
        }),
      });
      const data = await res.json() as ApiResponse<GeneratedContent>;
      if (data.success && data.data) {
        setGeneratedContent(data.data);
        setForm(prev => ({
          ...prev,
          content: data.data!.main_content,
          generated_content: data.data!.main_content,
          hashtags: data.data!.hashtags || [],
        }));
        setShowGenerateModal(false);
      } else {
        alert(data.error || 'Failed to generate content');
      }
    } catch (err) {
      console.error('Generate error:', err);
      alert('Failed to generate content');
    } finally {
      setIsGenerating(false);
    }
  };

  // Generate image with AI (costs 3 credits)
  const generateImage = async () => {
    if (!token) {
      alert('Please log in to generate images');
      return;
    }
    if (!form.content) {
      alert('Please write some content first. The AI uses your post content to generate a relevant image.');
      return;
    }
    setIsGeneratingImage(true);
    try {
      const res = await fetch(`${API_URL}/scheduled-posts/generate-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          content: form.content,
          style: 'modern, professional, visually striking',
          aspect_ratio: 'square',
        }),
      });
      const data = await res.json() as ApiResponse<{ image_url: string; credits_used?: number; credits_remaining?: number }>;
      if (data.success && data.data) {
        setForm(prev => ({
          ...prev,
          generated_image_url: data.data!.image_url,
        }));
        // Update credits in the auth store
        if (data.data.credits_remaining !== undefined) {
          updateCredits(data.data.credits_remaining);
        }
      } else {
        alert(data.error || 'Failed to generate image');
      }
    } catch (err) {
      console.error('Generate image error:', err);
      alert('Failed to generate image. Please check your connection and try again.');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // Save post
  const savePost = async (publish = false) => {
    if (!token || !form.content || form.target_platforms.length === 0 || !form.scheduled_at) {
      alert('Please fill in content, select platforms, and set a schedule time');
      return;
    }

    setIsSaving(true);
    try {
      const url = editingPost
        ? `${API_URL}/scheduled-posts/${editingPost.id}`
        : `${API_URL}/scheduled-posts`;
      const method = editingPost ? 'PUT' : 'POST';

      // Convert local datetime to UTC ISO string for consistent storage
      // The datetime-local input gives us a local time string like "2024-12-18T19:10"
      // We need to store this as UTC for proper comparison with datetime('now') in SQLite
      let scheduledAtUTC = form.scheduled_at;
      if (form.scheduled_at) {
        // The datetime-local input value is in the format "YYYY-MM-DDTHH:MM"
        // This represents the user's LOCAL time they want to schedule for
        // We need to convert this to UTC properly

        // Parse the datetime-local string components
        const [datePart, timePart] = form.scheduled_at.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes] = timePart.split(':').map(Number);

        // Create a Date object using the local time components
        // This correctly interprets the values as local time
        const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

        // Convert to ISO string (which outputs in UTC)
        scheduledAtUTC = localDate.toISOString();
      }

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...form,
          scheduled_at: scheduledAtUTC,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          status: publish ? 'scheduled' : 'draft',
        }),
      });

      const data = await res.json() as ApiResponse<ScheduledPost>;
      if (data.success) {
        setShowModal(false);
        resetForm();
        loadPosts();
        loadStats();
      } else {
        alert(data.error || 'Failed to save post');
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save post');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete post
  const deletePost = async (id: number) => {
    if (!confirm('Are you sure you want to delete this post?')) return;
    try {
      const res = await fetch(`${API_URL}/scheduled-posts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json() as ApiResponse<{ deleted: boolean }>;
      if (data.success) {
        loadPosts();
        loadStats();
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  // Cancel scheduled post
  const cancelPost = async (id: number) => {
    try {
      const res = await fetch(`${API_URL}/scheduled-posts/${id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json() as ApiResponse<ScheduledPost>;
      if (data.success) {
        loadPosts();
        loadStats();
      }
    } catch (err) {
      console.error('Cancel error:', err);
    }
  };

  // Publish post immediately
  const [isPublishing, setIsPublishing] = useState<number | null>(null);

  const publishPost = async (id: number) => {
    if (!confirm('Publish this post now to all selected platforms?')) return;

    setIsPublishing(id);
    try {
      const res = await fetch(`${API_URL}/scheduled-posts/${id}/publish`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json() as ApiResponse<{
        success: boolean;
        status: string;
        platforms: Array<{ platform: string; success: boolean; error?: string }>;
      }>;

      if (data.success && data.data) {
        const failedPlatforms = data.data.platforms.filter(p => !p.success);
        if (failedPlatforms.length > 0) {
          alert(`Post published with some errors:\n${failedPlatforms.map(p => `${p.platform}: ${p.error}`).join('\n')}`);
        } else {
          alert('Post published successfully to all platforms!');
        }
        loadPosts();
        loadStats();
      } else {
        alert(data.error || 'Failed to publish post');
      }
    } catch (err) {
      console.error('Publish error:', err);
      alert('Failed to publish post. Please try again.');
    } finally {
      setIsPublishing(null);
    }
  };

  // Edit post
  const editPost = (post: ScheduledPost) => {
    setEditingPost(post);

    // Convert UTC time from database to local time for the datetime-local input
    let localScheduledAt = '';
    if (post.scheduled_at) {
      const utcDate = new Date(post.scheduled_at);
      // Format as YYYY-MM-DDTHH:MM for datetime-local input (in local timezone)
      const year = utcDate.getFullYear();
      const month = String(utcDate.getMonth() + 1).padStart(2, '0');
      const day = String(utcDate.getDate()).padStart(2, '0');
      const hours = String(utcDate.getHours()).padStart(2, '0');
      const minutes = String(utcDate.getMinutes()).padStart(2, '0');
      localScheduledAt = `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    setForm({
      content: post.content,
      generated_content: post.generated_content || '',
      image_url: post.image_url || '',
      generated_image_url: post.generated_image_url || '',
      scheduled_at: localScheduledAt,
      target_platforms: post.target_platforms,
      hashtags: post.hashtags || [],
      link_url: post.link_url || '',
      ai_tone: post.ai_tone,
      ai_style: post.ai_style,
    });
    setShowModal(true);
  };

  // Reset form
  const resetForm = () => {
    setEditingPost(null);
    setForm({
      content: '',
      generated_content: '',
      image_url: '',
      generated_image_url: '',
      scheduled_at: '',
      target_platforms: [],
      hashtags: [],
      link_url: '',
      ai_tone: 'engaging',
      ai_style: 'human',
    });
    setGeneratedContent(null);
    setHashtagInput('');
  };

  // Add hashtag
  const addHashtag = () => {
    if (hashtagInput && !form.hashtags.includes(hashtagInput.replace('#', ''))) {
      setForm(prev => ({
        ...prev,
        hashtags: [...prev.hashtags, hashtagInput.replace('#', '')],
      }));
      setHashtagInput('');
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Status styling
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'posted':
        return 'bg-accent-green/20 text-accent-green';
      case 'scheduled':
        return 'bg-accent-cyan/20 text-accent-cyan';
      case 'posting':
        return 'bg-accent-orange/20 text-accent-orange';
      case 'failed':
        return 'bg-accent-red/20 text-accent-red';
      case 'cancelled':
        return isDark ? 'bg-dark-700 text-text-muted' : 'bg-slate-200 text-slate-500';
      default:
        return isDark ? 'bg-dark-700 text-text-muted' : 'bg-slate-200 text-slate-500';
    }
  };

  // Available platforms (show all common ones even if not connected)
  const availablePlatforms = [
    { id: 'twitter', name: 'Twitter / X', icon: <Twitter className="w-5 h-5" />, color: '#1DA1F2' },
    { id: 'linkedin', name: 'LinkedIn', icon: <Linkedin className="w-5 h-5" />, color: '#0A66C2' },
    { id: 'facebook', name: 'Facebook', icon: <Facebook className="w-5 h-5" />, color: '#1877F2' },
    { id: 'instagram', name: 'Instagram', icon: <Instagram className="w-5 h-5" />, color: '#E4405F' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-purple"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      className="space-y-6"
    >
      {/* Stats */}
      <motion.div variants={fadeInUp} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Scheduled', value: stats?.scheduled || 0, icon: Calendar, color: 'text-accent-cyan' },
          { label: 'Posted', value: stats?.posted || 0, icon: Check, color: 'text-accent-green' },
          { label: 'Drafts', value: stats?.drafts || 0, icon: Edit3, color: 'text-accent-orange' },
          { label: 'This Week', value: stats?.upcoming_week || 0, icon: Clock, color: 'text-accent-purple' },
        ].map((stat) => (
          <div key={stat.label} className={`rounded-2xl p-5 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{stat.value}</p>
                <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{stat.label}</p>
              </div>
              <stat.icon className={`w-8 h-8 ${stat.color} opacity-50`} />
            </div>
          </div>
        ))}
      </motion.div>

      {/* Actions & Filters */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          {['all', 'scheduled', 'posted', 'draft', 'failed'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f as typeof filter)}
              className={`px-4 py-2 rounded-xl text-sm font-medium capitalize transition-colors ${
                filter === f
                  ? 'bg-gradient-accent text-white'
                  : isDark ? 'bg-dark-800 text-text-secondary hover:bg-dark-700' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          <span>Schedule Post</span>
        </button>
      </motion.div>

      {/* Connected Platforms Info */}
      {integrations.length === 0 && (
        <motion.div variants={fadeInUp} className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800/50 border-accent-orange/30' : 'bg-orange-50 border-orange-200'}`}>
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-accent-orange flex-shrink-0 mt-0.5" />
            <div>
              <h3 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>No Social Media Accounts Connected</h3>
              <p className={`text-sm mb-3 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                Connect your social media accounts (Twitter, LinkedIn, Facebook, Instagram) in the Integrations section to enable auto-posting.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  {availablePlatforms.map((p) => (
                    <div
                      key={p.id}
                      className={`p-2 rounded-lg ${isDark ? 'bg-dark-700' : 'bg-white'}`}
                      style={{ color: p.color }}
                    >
                      {p.icon}
                    </div>
                  ))}
                </div>
                <a
                  href="/dashboard/integrations"
                  className="btn-primary text-sm px-4 py-2"
                >
                  Connect Accounts
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Posts List */}
      <motion.div variants={fadeInUp} className="space-y-3">
        {posts.length === 0 ? (
          <div className={`text-center py-16 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200'}`}>
            <Calendar className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>No scheduled posts</h3>
            <p className={`mb-4 ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
              Create your first post and schedule it to publish automatically
            </p>
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              <span>Create Post</span>
            </button>
          </div>
        ) : (
          posts.map((post) => (
            <div
              key={post.id}
              className={`rounded-2xl p-4 sm:p-5 border transition-all ${isDark ? 'bg-dark-800 border-border hover:border-accent-purple/30' : 'bg-white border-slate-200 hover:border-accent-purple/30 shadow-sm'}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                {/* Image preview - only show if we have a valid (non-expired) image URL */}
                {(() => {
                  const safeUrl = getSafeImageUrl(post.image_url) || getSafeImageUrl(post.generated_image_url);
                  const hasExpiredImage = !safeUrl && (post.image_url || post.generated_image_url);

                  if (safeUrl) {
                    return (
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden flex-shrink-0 bg-dark-700 flex items-center justify-center">
                        <img
                          src={safeUrl}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Hide image on error (any other error)
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            // Show a placeholder icon instead
                            const parent = target.parentElement;
                            if (parent && !parent.querySelector('.image-error-icon')) {
                              const icon = document.createElement('div');
                              icon.className = 'image-error-icon text-text-muted';
                              icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';
                              parent.appendChild(icon);
                            }
                          }}
                        />
                      </div>
                    );
                  } else if (hasExpiredImage) {
                    // Show placeholder for expired DALL-E images (without trying to load them)
                    return (
                      <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                        <div className="text-text-muted" title="Image expired">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
                            <circle cx="9" cy="9" r="2"/>
                            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
                          </svg>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col gap-3">
                    <div className="flex-1">
                      <p className={`font-medium line-clamp-2 text-sm sm:text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {post.content.substring(0, 150)}{post.content.length > 150 ? '...' : ''}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-2">
                        <span className={`text-xs sm:text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                          {formatDate(post.scheduled_at)}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(post.status)}`}>
                          {post.status}
                        </span>
                      </div>
                    </div>

                    {/* Platforms */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {post.target_platforms.map((platform) => (
                        <div
                          key={platform}
                          className={`p-1.5 rounded-lg ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}
                          style={{ color: platformColors[platform] }}
                          title={platform}
                        >
                          {platformIcons[platform] || <Send className="w-4 h-4" />}
                        </div>
                      ))}
                      {post.hashtags && post.hashtags.length > 0 && (
                        <span className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                          <Hash className="w-3 h-3 inline" /> {post.hashtags.length} hashtags
                        </span>
                      )}
                    </div>

                    {/* Actions - moved to bottom for mobile */}
                    <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-border sm:border-0 sm:pt-0">
                      {['draft', 'scheduled'].includes(post.status) && (
                        <>
                          {/* Post Now button */}
                          <button
                            onClick={() => publishPost(post.id)}
                            disabled={isPublishing === post.id}
                            className={`px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors ${
                              isPublishing === post.id
                                ? 'bg-accent-green/50 text-white cursor-wait'
                                : 'bg-accent-green text-white hover:bg-accent-green/80'
                            }`}
                            title="Publish Now"
                          >
                            {isPublishing === post.id ? (
                              <Loader2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 animate-spin" />
                            ) : (
                              <Send className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                            )}
                            <span className="hidden xs:inline">{isPublishing === post.id ? 'Publishing...' : 'Post Now'}</span>
                          </button>
                          <button
                            onClick={() => editPost(post)}
                            className={`p-1.5 sm:p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700 text-text-secondary hover:text-white' : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'}`}
                            title="Edit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          {post.status === 'scheduled' && (
                            <button
                              onClick={() => cancelPost(post.id)}
                              className={`p-1.5 sm:p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700 text-text-secondary hover:text-accent-orange' : 'hover:bg-slate-100 text-slate-400 hover:text-orange-500'}`}
                              title="Cancel"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </>
                      )}
                      <button
                        onClick={() => deletePost(post.id)}
                        className={`p-1.5 sm:p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700 text-text-secondary hover:text-accent-red' : 'hover:bg-slate-100 text-slate-400 hover:text-red-500'}`}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Error message */}
                  {post.status === 'failed' && post.error_message && (
                    <p className="text-sm text-accent-red mt-2">{post.error_message}</p>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </motion.div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl ${isDark ? 'bg-dark-800' : 'bg-white'} p-6`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {editingPost ? 'Edit Post' : 'Schedule New Post'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className={`p-2 rounded-xl ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* AI Generate Button */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowGenerateModal(true)}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <Sparkles className="w-5 h-5 text-accent-purple" />
                    <span>Generate with AI</span>
                  </button>
                  <span className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                    Create engaging, human-like content
                  </span>
                </div>

                {/* Content */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                    Post Content *
                  </label>
                  <textarea
                    value={form.content}
                    onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
                    rows={5}
                    className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-accent-purple/50 ${isDark ? 'bg-dark-900 border-border text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                    placeholder="Write your post content here or generate it with AI..."
                  />
                  <p className={`text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                    {form.content.length} characters
                  </p>
                </div>

                {/* Image Section */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                    Image
                  </label>
                  <div className="flex items-start gap-4">
                    {(form.image_url || form.generated_image_url) ? (
                      <div className="relative w-32 h-32 rounded-xl overflow-hidden">
                        <img
                          src={form.image_url || form.generated_image_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={() => setForm(prev => ({ ...prev, image_url: '', generated_image_url: '' }))}
                          className="absolute top-1 right-1 p-1 bg-black/50 rounded-lg text-white hover:bg-black/70"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className={`w-32 h-32 rounded-xl border-2 border-dashed flex items-center justify-center ${isDark ? 'border-border' : 'border-slate-200'}`}>
                        <ImageIcon className={`w-8 h-8 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                      </div>
                    )}
                    <div className="space-y-2">
                      <input
                        type="url"
                        value={form.image_url}
                        onChange={(e) => setForm(prev => ({ ...prev, image_url: e.target.value }))}
                        className={`w-full px-4 py-2 rounded-xl border ${isDark ? 'bg-dark-900 border-border text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                        placeholder="Paste image URL..."
                      />
                      <button
                        onClick={generateImage}
                        disabled={!form.content || isGeneratingImage}
                        className="btn-secondary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {isGeneratingImage ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                        <span>Generate Image with AI (3 credits)</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Platforms */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                    Post to Platforms *
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {availablePlatforms.map((platform) => {
                      const isSelected = form.target_platforms.includes(platform.id);
                      const isConnected = integrations.some(i => i.provider === platform.id);
                      return (
                        <button
                          key={platform.id}
                          onClick={() => {
                            setForm(prev => ({
                              ...prev,
                              target_platforms: isSelected
                                ? prev.target_platforms.filter(p => p !== platform.id)
                                : [...prev.target_platforms, platform.id],
                            }));
                          }}
                          className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all ${
                            isSelected
                              ? 'border-accent-purple bg-accent-purple/20'
                              : isDark ? 'border-border hover:border-accent-purple/50' : 'border-slate-200 hover:border-accent-purple/50'
                          }`}
                          style={{ color: isSelected ? platform.color : undefined }}
                        >
                          {platform.icon}
                          <span className={isSelected ? '' : isDark ? 'text-text-secondary' : 'text-slate-600'}>
                            {platform.name}
                          </span>
                          {!isConnected && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-accent-orange/20 text-accent-orange">
                              Not Connected
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Schedule */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                    Schedule Date & Time *
                  </label>
                  <DateTimePicker
                    value={form.scheduled_at}
                    onChange={(value) => setForm(prev => ({ ...prev, scheduled_at: value }))}
                    minDate={new Date().toISOString()}
                    placeholder="Select date and time"
                  />
                </div>

                {/* Hashtags */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                    Hashtags
                  </label>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={hashtagInput}
                      onChange={(e) => setHashtagInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addHashtag())}
                      className={`flex-1 px-4 py-2 rounded-xl border ${isDark ? 'bg-dark-900 border-border text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                      placeholder="Add hashtag..."
                    />
                    <button
                      onClick={addHashtag}
                      className="btn-secondary px-4 py-2"
                    >
                      Add
                    </button>
                  </div>
                  {form.hashtags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {form.hashtags.map((tag, i) => (
                        <span
                          key={i}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${isDark ? 'bg-dark-700 text-text-secondary' : 'bg-slate-100 text-slate-600'}`}
                        >
                          #{tag}
                          <button
                            onClick={() => setForm(prev => ({ ...prev, hashtags: prev.hashtags.filter((_, idx) => idx !== i) }))}
                            className="hover:text-accent-red"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Link */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                    Link URL (optional)
                  </label>
                  <div className="flex items-center gap-2">
                    <Link2 className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                    <input
                      type="url"
                      value={form.link_url}
                      onChange={(e) => setForm(prev => ({ ...prev, link_url: e.target.value }))}
                      className={`flex-1 px-4 py-2 rounded-xl border ${isDark ? 'bg-dark-900 border-border text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                      placeholder="https://..."
                    />
                  </div>
                </div>

                {/* AI Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                      Tone
                    </label>
                    <select
                      value={form.ai_tone}
                      onChange={(e) => setForm(prev => ({ ...prev, ai_tone: e.target.value }))}
                      className={`w-full px-4 py-2 rounded-xl border ${isDark ? 'bg-dark-900 border-border text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                    >
                      <option value="engaging">Engaging</option>
                      <option value="professional">Professional</option>
                      <option value="casual">Casual</option>
                      <option value="humorous">Humorous</option>
                      <option value="inspirational">Inspirational</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                      Style
                    </label>
                    <select
                      value={form.ai_style}
                      onChange={(e) => setForm(prev => ({ ...prev, ai_style: e.target.value }))}
                      className={`w-full px-4 py-2 rounded-xl border ${isDark ? 'bg-dark-900 border-border text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                    >
                      <option value="human">Human-like</option>
                      <option value="formal">Formal</option>
                      <option value="conversational">Conversational</option>
                      <option value="storytelling">Storytelling</option>
                    </select>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                  <button
                    onClick={() => setShowModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => savePost(false)}
                    disabled={isSaving}
                    className="btn-secondary flex items-center gap-2"
                  >
                    {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                    Save as Draft
                  </button>
                  <button
                    onClick={() => savePost(true)}
                    disabled={isSaving || !form.content || form.target_platforms.length === 0 || !form.scheduled_at}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50"
                  >
                    {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                    <Calendar className="w-4 h-4" />
                    Schedule Post
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Generate Modal */}
      <AnimatePresence>
        {showGenerateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
            onClick={() => setShowGenerateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`w-full max-w-lg rounded-3xl ${isDark ? 'bg-dark-800' : 'bg-white'} p-6`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-gradient-accent">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Generate with AI
                  </h3>
                </div>
                <button
                  onClick={() => setShowGenerateModal(false)}
                  className={`p-2 rounded-xl ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                    What's your post about?
                  </label>
                  <textarea
                    value={generateTopic}
                    onChange={(e) => setGenerateTopic(e.target.value)}
                    rows={3}
                    className={`w-full px-4 py-3 rounded-xl border ${isDark ? 'bg-dark-900 border-border text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                    placeholder="e.g., Announcing our new AI feature that helps businesses automate customer support..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                      Tone
                    </label>
                    <select
                      value={form.ai_tone}
                      onChange={(e) => setForm(prev => ({ ...prev, ai_tone: e.target.value }))}
                      className={`w-full px-4 py-2 rounded-xl border ${isDark ? 'bg-dark-900 border-border text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                    >
                      <option value="engaging">Engaging</option>
                      <option value="professional">Professional</option>
                      <option value="casual">Casual</option>
                      <option value="humorous">Humorous</option>
                      <option value="inspirational">Inspirational</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                      Style
                    </label>
                    <select
                      value={form.ai_style}
                      onChange={(e) => setForm(prev => ({ ...prev, ai_style: e.target.value }))}
                      className={`w-full px-4 py-2 rounded-xl border ${isDark ? 'bg-dark-900 border-border text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                    >
                      <option value="human">Human-like</option>
                      <option value="conversational">Conversational</option>
                      <option value="storytelling">Storytelling</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={generatePost}
                  disabled={!generateTopic || isGenerating}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate Post
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
